<template>
  <div class="filter-single">
    <ButtonClassic
      data-bs-toggle="collapse"
      :data-bs-target="`#filter_${filter}`"
      :label="label"
      color="primary"
      icon="right"
      size="large"
      @click="isVisible = !isVisible"
    >
      <template #right-icon>
        <UilAngleDown
          v-if="!isVisible"
          size="24"
        />
        <UilAngleUp
          v-else
          size="24"
        />
      </template>
    </ButtonClassic>
    <div
      :id="`filter_${filter}`"
      class="collapse"
    >
      <ButtonCheckbox
        v-for="(option, index) in options"
        :key="`option-${index}-${option.id}`"
        :value="option.libelle"
        :name="`option-${option.libelle}-${option.id}`"
        :label="option.libelle"
        :checked="isChecked(option.id, filter)"
        @change="$emit('change', { value: $event, option: option, })"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import ButtonClassic from "../../../components/buttons/ButtonClassic.vue";
import ButtonCheckbox from "../../../components/buttons/ButtonCheckbox.vue";

import { UilAngleDown, UilAngleUp } from "@lde/vue-unicons";

/**
 * Affiche un groupe de filtres avec un titre.
 */
export default defineComponent({
  name: "FilterSingle",
  components: {
    ButtonClassic,
    ButtonCheckbox,
    UilAngleDown,
    UilAngleUp,
  },
  props: {
    /**
     * Définit si le groupe de filtres est replié/déplié.
     */
    isCollapsed: {
      type: Boolean,
      default: false,
    },
    /**
     * Label qui sera affiché comme titre du groupe des filtres.
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * Slug du filtre actuel.
     */
    filter: {
      type: String,
      default: "",
    },
    /**
     * Options de ce filtre qui génèrent les checkboxes.
     */
    options: {
      type: Array,
      default: () => [],
    },
    /**
     * Méthode qui permet de savoir si une certaine option est cochée ou non.
     */
    isChecked: {
      type: Function,
      default: () => ({}),
    },
  },
  emits: [
    /**
     * Déclenché au changement d'état de la checkbox.
     * @param {Object} - Valeur et id de l'option.
     */
    "change",
    /**
     * Déclenché quand on replie/déplie un groupe de filtres.
     * @param {Boolean} newV Si true, le groupe est visible.
     */
    "collapse",
    /**
     * Déclenché quand on est au bout du scroll du filtre courant.
     */
    "scroll-end",
  ],
  computed: {
    isVisible: {
      get() {
        return this.isCollapsed;
      },
      set(newV) {
        this.$emit("collapse", newV);
      },
    },
  },
  mounted() {
    window.addEventListener("scroll", this.onScroll, true);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll, true);
  },
  methods: {
    /**
     * Envoie un emit une fois qu'on est tout en bas de la liste des filtres.
     * @param {Object} event Événement natif du scroll.
     */
    onScroll(event) {
      const { scrollTop, scrollHeight, offsetHeight } = event.target;

      if (scrollTop >= scrollHeight - offsetHeight) {
        this.$emit("scroll-end");
      }
    },
  },
});
</script>

<style lang="scss">
@use "../../../assets/styles/_variables.scss" as var;

.filter-single {
  &:not(:last-child) { margin-bottom: var.$space-x-tiny; }
  &:last-child { margin-bottom: var.$space-xx-large; }

  .button-classic {
    justify-content: space-between;
    padding: 0;
    width: 100%;
    span { font-size: var.$font-medium; }
  }

  .button-toggle {
    display: flex;
    padding: var.$space-x-tiny 0 0.7rem; // pas 0.8rem à cause de la bordure
    border-bottom: 1px solid transparent;
    &.checked { border-bottom-color: var.$orange; }
  }
}
</style>
